import React, { useEffect, useMemo, useState } from 'react'
import { Alert, notification, Modal } from 'antd'
import { RouteComponentProps } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ApplicationState } from 'src/store'
import { ButtonTitled, PageHeader } from 'src/components/Ant'
import { getFullRequest } from 'src/core/store/ducks/requests/actions'
import { IconEditRequestTitle } from 'src/components/atoms/edit-request-title'
import { Request, RequestApprovalStatus, RequestUserData } from 'src/types'
import { SaveObservationRequest } from 'src/services/api/requests'
import { setApprovalStatus } from 'src/core/store/ducks/approvals/actions'
import SegmentControl, { Segments } from 'src/components/SegmentControl'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'
import * as CSS from './styles'
import { shouldShowRDVButton } from '../utils'
import ModalAction, { ModalActionType } from '../../components/modal-action'
import RDVButton from '../../components/RDVButton'
import Receipts from '../../components/Receipts'
import RequestSummary from '../../components/RequestSummary'

interface Params {
  request_id?: string
}

type SegmentKeys = 'receipts' | 'summary'

type SummarySegments = Segments<SegmentKeys>

export default function ApprovalsSummary({
  history,
  match: {
    params: { request_id }
  }
}: RouteComponentProps<Params>) {
  const {
    approvals,
    me: {
      user: { auditor, role_group, tenant_id, user_level_type }
    },
    requests
  } = useSelector((state: ApplicationState) => ({
    approvals: state.approvals,
    me: state.me,
    requests: state.requests
  }))

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [action, setAction] = useState<ModalActionType | null>(null)
  const [changingStatus, setChangingStatus] = useState<boolean>(false)
  const [current, setCurrent] = useState<SegmentKeys>('summary')
  const [saving, setSaving] = useState<boolean>(false)
  const [savingFullRequest, setSavingFullRequest] = useState<boolean>(false)

  const isSuperAdmin = ['SUPER_ADMIN'].includes(user_level_type)
  const policyRule = useReasonPolicyRule(
    requests.fullRequest?.request_user_data[0].reason_id
  )
  const enableCancelAction = ['APPROVAL_PENDING', 'APPROVAL_REPROVED'].includes(
    requests.fullRequest?.status
  )
  const enableApprovalAction = ['APPROVAL_PENDING', 'UNDERWAY'].includes(
    requests.fullRequest?.status
  )
  const enableReproveAction =
    requests.fullRequest?.status === 'APPROVAL_PENDING'

  let enableAdvancesEdit = enableApprovalAction

  if (
    policyRule?.disable_advance_retroactive &&
    requests.fullRequest?.requested_on <
      moment({
        hour: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0
      }).unix()
  ) {
    enableAdvancesEdit = false
  }

  async function saveRequest(request: Request) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.summary.change-observation-confirm'),
      onCancel() {},
      onOk: async () => {
        await SaveObservationRequest({
          organization_id: request.organization_id,
          request_id: request.request_id,
          obs: request.obs
        })
        setTimeout(() => {
          dispatch(getFullRequest(request.request_id, 'approvals'))
        }, 100)
      },
      title: t('common.actions.delete.title')
    })
    return await new Promise(() => {})
  }

  const hasAnyService = requests?.fullRequest?.request_tickets?.some(
    ticket =>
      ['AIR', 'CARRO', 'HOTEL'].includes(ticket.request_ticket_type) &&
      ticket.request_ticket_receipts.length
  )
  const segments = useMemo<SummarySegments>(
    () => ({
      summary: {
        disabled: false,
        title: 'web.summary.title',
        /**
         * @todo
         * re add edit buttons
         */
        renderContent: () => (
          <RequestSummary
            enableAdvancesEdit={enableAdvancesEdit}
            enableBillingEdit={enableApprovalAction}
            enableBudget={policyRule?.enable_budget}
            enableEditAirs={enableApprovalAction && hasAnyService}
            enableEditHotels={enableApprovalAction && hasAnyService}
            enableEditVehicles={enableApprovalAction && hasAnyService}
            enableItinerariesEdit={enableApprovalAction}
            enableSharingEdit={
              isSuperAdmin ? isSuperAdmin : enableApprovalAction
            }
            originRequest="approvals"
            request={requests.fullRequest}
            saveRequest={saveRequest}
            enableEditObservation={
              requests.fullRequest.status === 'APPROVAL_PENDING'
            }
          />
        )
      },
      receipts: {
        disabled: false,
        title: 'web.receipts.title',
        renderContent: () => (
          <Receipts approvals request={requests.fullRequest} />
        )
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enableApprovalAction, enableAdvancesEdit, policyRule, requests.fullRequest]
  )

  useEffect(() => {
    dispatch(getFullRequest(request_id, 'approvals'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request_id])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== saving && approvals) {
      setSaving(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading && !requests.saveFullRequestError) {
        dispatch(getFullRequest(request_id, 'approvals'))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  useEffect(() => {
    if (approvals.setStatusLoading !== changingStatus) {
      setChangingStatus(approvals.setStatusLoading)

      if (!approvals.setStatusLoading) {
        if (approvals.setStatusError) {
          notification.error({
            duration: 7,
            message: approvals.setStatusError,
            placement: 'bottomRight'
          })
        } else {
          notification.success({
            duration: 7,
            message: approvals.setStatusSuccess,
            placement: 'bottomRight'
          })
          history.push('/approvals')
        }

        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals.setStatusLoading])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== savingFullRequest) {
      setSavingFullRequest(requests.saveFullRequestLoading)
      if (!requests.saveFullRequestLoading) {
        if (requests.saveFullRequestError) {
          notification.error({
            duration: 5,
            message: requests.saveFullRequestError,
            placement: 'bottomRight'
          })
        } else {
          notification.success({
            duration: 7,
            message: requests.saveFullRequestSuccess,
            placement: 'bottomRight'
          })
        }
        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  function toggleAction(action: 'approve' | 'cancel' | 'disapprove' | null) {
    if (!action) {
      setAction(null)
      return
    }

    let actionTitle = ''
    switch (action) {
      case 'approve':
        actionTitle = t('web.approvals.modal-accept')
        break
      case 'disapprove':
        actionTitle = t('web.approvals.modal-decline')
        break
      case 'cancel':
        actionTitle = t('web.approvals.modal-cancel')
    }

    setAction({
      action,
      title: actionTitle
    })
  }

  if (requests.fullRequestLoading || approvals.loading) {
    return <CSS.Spin />
  }

  const hasError = requests.fullRequestError || approvals.error
  if (hasError) {
    return <Alert message={hasError} showIcon type="error" />
  }

  if (!requests.fullRequest) {
    const message = t('common.response.unexpected-error')
    return <Alert message={message} showIcon type="error" />
  }

  function userReduce(acc: RequestUserData[], cur: RequestUserData) {
    const canApprove =
      (cur.status === 'APPROVAL_PENDING' && cur.approval) ||
      (cur.status === 'APPROVAL_REPROVED' && cur.allowed)

    if (!canApprove) {
      return acc
    }

    const user_data = cur.request_approvals.filter(
      approval => approval.status === 'PENDING'
    )
    return [...acc, { ...cur, request_approvals: user_data }]
  }

  function handleSubmit(justification: string, users: RequestUserData[]) {
    let status: RequestApprovalStatus
    switch (action.action) {
      case 'approve':
        status = 'APPROVED'
        break
      case 'cancel':
        status = 'CANCELED'
        break
      case 'disapprove':
        status = 'REPROVED'
        break
    }

    dispatch(
      setApprovalStatus({
        justification,
        request_approval_ids: users.reduce(
          (acc, { request_approvals }) => [
            ...acc,
            ...request_approvals
              .filter(
                ({ situation, status: request_approval_status }) =>
                  situation === 'ACTIVE' &&
                  request_approval_status === 'PENDING'
              )
              .map(({ request_approval_id }) => request_approval_id)
          ],
          []
        ),
        request_id,
        status
      })
    )
  }

  const canAuditorEdit =
    auditor && [334081949181, 597990758089].includes(+tenant_id)

  return (
    <>
      <PageHeader
        extra={[
          enableCancelAction && (
            <ButtonTitled
              data-test-id="summary-approval-cancel"
              icon="delete"
              key="btnCancel"
              onClick={() => toggleAction('cancel')}
              size="default"
              tooltip="web.approvals.cancel"
              type="default"
            />
          ),
          enableReproveAction && (
            <ButtonTitled
              data-test-id="summary-approval-disapprove"
              icon="close"
              key="btnDisapprove"
              onClick={() => toggleAction('disapprove')}
              size="default"
              tooltip="web.approvals.decline"
              type="danger"
            />
          ),
          enableApprovalAction && (
            <ButtonTitled
              data-test-id="summary-approval-submit"
              icon="check"
              key="btnApprove"
              onClick={() => toggleAction('approve')}
              size="default"
              tooltip="web.approvals.accept"
            />
          ),
          shouldShowRDVButton({
            request: requests.fullRequest,
            roleGroup: role_group
          }) && <RDVButton key="btnRDV" request={requests.fullRequest} />
        ]}
        onBack={() => history.goBack()}
        subTitle={
          <>
            {`#${requests.fullRequest.request_number} - ${requests.fullRequest.title} `}
            {canAuditorEdit && (
              <IconEditRequestTitle
                requestId={requests.fullRequest.request_id}
                organizationId={requests.fullRequest.organization_id}
                update={() => dispatch(getFullRequest(request_id, 'approvals'))}
                title={requests.fullRequest.title}
              />
            )}
          </>
        }
        title="web.approvals.title"
      />
      <CSS.ContainerSegmentControl>
        <SegmentControl
          defaultValue="summary"
          onChange={({ target: { value } }) => {
            setCurrent(value)
          }}
          segments={segments}
          value={current}
        />
      </CSS.ContainerSegmentControl>
      {segments[current].renderContent()}
      <ModalAction
        handleSubmit={handleSubmit}
        isLoading={
          approvals.setStatusLoading ||
          requests.fullRequestLoading ||
          approvals.loading
        }
        modalAction={action}
        request_user_data={requests.fullRequest?.request_user_data}
        toggleAction={toggleAction}
        userReduce={userReduce}
      />
    </>
  )
}
